import React from 'react';
import { useLocation } from 'react-router-dom';
import SEO from '../SEO';
import { readBlogArticleSnapshot } from './blogArticleSnapshot';

/** Keep the original server article while its lazy React module is unavailable. */
export default function BlogArticleModuleFallback({ children }: { children: React.ReactNode }) {
    const { pathname } = useLocation();
    const snapshot = readBlogArticleSnapshot(pathname);
    if (!snapshot) return <>{children}</>;
    return <>
        <SEO title={snapshot.title} description={snapshot.description} canonicalPath={pathname}
            type="article" image={snapshot.image} hreflangs={snapshot.hreflangs} />
        <div dangerouslySetInnerHTML={{ __html: snapshot.html }} />
    </>;
}

class ArticleModuleErrorBoundary extends React.Component<{
    children: React.ReactNode;
    fallback: React.ReactNode | null;
}, { error: Error | null }> {
    state = { error: null as Error | null };
    static getDerivedStateFromError(error: Error) { return { error }; }
    render() {
        const error = this.state.error;
        if (!error) return this.props.children;
        const isModuleError = error.name === 'ChunkLoadError'
            || /dynamically imported module|Loading chunk|Importing a module script failed/.test(error.message);
        if (!isModuleError || !this.props.fallback) throw error;
        return this.props.fallback;
    }
}

/** Catch article module failures before the application's automatic reload handler. */
export function BlogArticleModuleBoundary({ children }: { children: React.ReactNode }) {
    const { pathname } = useLocation();
    const fallback = readBlogArticleSnapshot(pathname)
        ? <BlogArticleModuleFallback>{null}</BlogArticleModuleFallback> : null;
    return <ArticleModuleErrorBoundary key={pathname} fallback={fallback}>{children}</ArticleModuleErrorBoundary>;
}
